/**
 * ComingSoonDialog — pre-launch waitlist capture for the public /discover route.
 *
 * Follows the page theme. It used to opt into dark with `force-dark` on the
 * dialog node, but Tailwind's colour tokens resolve on `:root`, so that class
 * only reached the literal `input` override: a light card with a black email
 * field and a white-to-black star panel. The plate on the left is the same
 * photograph the sign-in page uses, so the two still read as one family.
 *
 * The email field takes focus in `onOpenAutoFocus`, inside the tap that opened
 * the dialog, because iOS only raises the keyboard for a focus made during a
 * user gesture. While the keyboard is up the dialog re-centres on the visible
 * area (`useKeyboardViewport`) so the field is not left underneath it.
 */

import { ArrowRightIcon, CheckIcon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { LogoMark } from '@/components/ui/logo';
import { Spinner } from '@/components/ui/spinner';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';
import { cn } from '@/lib/utils';
import { waitlistService } from '@/services/data/waitlistService';
import { showErrorToast, showSuccessToast } from '@/utils/toast';

interface ComingSoonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

type ViewState = 'form' | 'success';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PLATE_IMAGE_URL = '/art-lab/golden-gate-1937-pd.jpg';

function readUtm(): {
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
} {
  if (typeof window === 'undefined') {
    return {
      referrer: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
    };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    referrer: document.referrer || null,
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
  };
}

export function ComingSoonDialog({
  open,
  onOpenChange,
  source = 'discover_coming_soon',
}: ComingSoonDialogProps) {
  const [email, setEmail] = useState('');
  const [view, setView] = useState<ViewState>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const keyboard = useKeyboardViewport(open);

  const signInHref = `/auth?return_to=${encodeURIComponent(location.pathname + location.search)}`;

  useEffect(() => {
    if (open) {
      setEmail('');
      setView('form');
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (keyboard) inputRef.current?.scrollIntoView({ block: 'nearest' });
  }, [keyboard]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = email.trim();
      if (!EMAIL_RE.test(trimmed)) {
        setError('Enter a valid email address.');
        return;
      }
      setError(null);
      setIsSubmitting(true);
      try {
        const utm = readUtm();
        const result = await waitlistService.submit({
          email: trimmed,
          source,
          referrer: utm.referrer,
          utmSource: utm.utm_source,
          utmMedium: utm.utm_medium,
          utmCampaign: utm.utm_campaign,
          utmContent: utm.utm_content,
          userAgent:
            typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : null,
        });
        if (!result.ok) {
          showErrorToast('Could not save your email. Please try again.');
          setError('message' in result ? result.message : null);
          return;
        }
        showSuccessToast(
          result.alreadyOnList ? "You're already on the list." : "You're on the list.",
          result.alreadyOnList
            ? "We'll send a single email when Mora opens up."
            : "We'll let you know the moment Mora goes live.",
        );
        inputRef.current?.blur();
        setView('success');
      } catch (caught) {
        const message =
          caught instanceof Error ? caught.message : "We couldn't complete that — try again.";
        showErrorToast('Could not save your email. Please try again.');
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, source],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="wide"
        className="gap-0 overflow-hidden rounded-2xl p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
        style={
          keyboard
            ? { top: keyboard.offsetTop + keyboard.height / 2, maxHeight: keyboard.height - 24 }
            : undefined
        }
      >
        <div
          className={cn(
            'grid w-full grid-cols-1 lg:grid-cols-[5fr_6fr]',
            !keyboard && 'lg:min-h-130',
          )}
        >
          <div className="hidden p-3 lg:block">
            <div className="relative size-full overflow-hidden rounded-xl bg-muted ring-1 ring-border/40">
              <img
                src={PLATE_IMAGE_URL}
                alt=""
                aria-hidden
                decoding="async"
                className="absolute inset-0 size-full object-cover dark:opacity-80"
              />
            </div>
          </div>

          <div className="flex flex-col justify-center px-6 py-10 sm:px-12">
            <div className="mx-auto w-full max-w-sm">
              <LogoMark className="mb-8 h-[22px] w-auto text-foreground" />

              {view === 'form' ? (
                <>
                  <div className="mb-6 space-y-2">
                    <DialogTitle>Mora opens soon</DialogTitle>
                    <DialogDescription>
                      Sign-ups are paused during the private beta. Leave your email and we&rsquo;ll
                      write once, when Mora opens.
                    </DialogDescription>
                  </div>

                  <form onSubmit={handleSubmit} noValidate className="space-y-3">
                    <div className="space-y-1.5">
                      <label htmlFor="coming-soon-email" className="sr-only">
                        Email address
                      </label>
                      <Input
                        ref={inputRef}
                        id="coming-soon-email"
                        name="email"
                        type="email"
                        size="lg"
                        autoComplete="email"
                        inputMode="email"
                        enterKeyHint="send"
                        spellCheck={false}
                        autoCapitalize="off"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (error) setError(null);
                        }}
                        disabled={isSubmitting}
                        aria-invalid={!!error}
                        aria-describedby={error ? 'coming-soon-error' : undefined}
                      />
                      {error ? (
                        <p id="coming-soon-error" role="alert" className="text-xs text-destructive">
                          {error}
                        </p>
                      ) : null}
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting || !email.trim()}
                      className="w-full md:h-10"
                    >
                      {isSubmitting ? (
                        <Spinner size="sm" className="text-current" />
                      ) : (
                        <>
                          Notify me
                          <ArrowRightIcon className="size-4" weight="regular" />
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="mt-4 text-detail text-muted-foreground">
                    One email when we launch. No spam, unsubscribe anytime.
                  </p>

                  <p className="mt-8 border-t border-border/40 pt-5 text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <Link
                      to={signInHref}
                      onClick={() => onOpenChange(false)}
                      className="font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      Sign in
                    </Link>
                  </p>
                </>
              ) : (
                <div className="flex flex-col items-start gap-6">
                  <div className="flex size-10 items-center justify-center rounded-full bg-success/10">
                    <CheckIcon className="size-5 text-success" weight="regular" />
                  </div>
                  <div className="space-y-2">
                    <DialogTitle>You&rsquo;re on the list</DialogTitle>
                    <DialogDescription>
                      We&rsquo;ll send a single email when Mora opens. Discover stays open to
                      everyone in the meantime.
                    </DialogDescription>
                  </div>
                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="w-full md:h-10"
                  >
                    Back to Discover
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
